import { get } from "svelte/store";
import {
  NavigationType,
  sessionStore,
  setSelectedGift,
  clearSelectedGifts,
  removeMultipleSelectionItem,
  type Session
} from "../../stores/session";
import { startPolling } from "../steps/wait";
import { waitForNextFinishedPhase } from "./wait/handlers";
import { navigateTo, navigateToPath } from "../../routing";
import { buildMenuItems, type MenuItem } from "./menuItems";
import { saveUserSettings } from "../../apiLoader/user";
import type { CampaignUser } from "../../../types/campaign";
import { activateAndContinueWorkflow } from "../activateAndContinue";
import { getCampaignUser } from "../campaignUserManager";
import { getCurrentPhase } from "../../utils";
import PATHS from "../../constants/routePaths";

export const ActivationHandler = {
  activateAndContinue: async () => {
    await activateAndContinueWorkflow();

    const currentPhasePromise = new Promise<{ phase: string | null; campaignUser: CampaignUser }>((resolve) => {
      const checkAvailabilityHandler = waitForNextFinishedPhase(resolve);
      startPolling(checkAvailabilityHandler);
    });

    const { phase: currentPhase, campaignUser: currentCampaignUser } = await currentPhasePromise;
    return { currentPhase, currentCampaignUser };
  },
  activateIfNeeded: (campaignUser: CampaignUser) => {
    const beforeActivatePhase = getCurrentPhase(campaignUser.metadata);

    if (["global-phase", "rollback-phase"].includes(beforeActivatePhase)) {
      return ActivationHandler.activateAndContinue();
    } else {
      return { currentPhase: beforeActivatePhase, currentCampaignUser: campaignUser };
    }
  },
  handleUnexpectedPhase: (phase: string | null) => {
    return phase?.startsWith("thank-you") ? navigateToPath(PATHS.SUCCESS) : navigateTo(NavigationType.Next);
  }
};

export const getMenuData = async () => {
  const campaignUser = await getCampaignUser();
  const { currentPhase, currentCampaignUser } = await ActivationHandler.activateIfNeeded(campaignUser);

  if (currentPhase?.startsWith("complete-order")) {
    if (!currentCampaignUser?.metadata?.menu_items || !currentCampaignUser?.metadata?.menu_items.length) {
      clearSelectedGifts();
      navigateTo(NavigationType.Next);
    } else {
      return {
        requiredSelectionCount: currentCampaignUser.metadata?.required_selection_count,
        menuData: buildMenuItems(currentCampaignUser) ?? { menuItems: [], socialGiftBundleItems: [] }
      };
    }
  } else {
    clearSelectedGifts();
    ActivationHandler.handleUnexpectedPhase(currentPhase);
  }

  return {
    menuData: { menuItems: [], socialGiftBundleItems: [] }
  };
};

export const nextStep = async (selectedItem: MenuItem | undefined, selectedVariation: string | undefined) => {
  if (!selectedItem) return;

  const visibleVariation = selectedItem.variations?.find(
    (variation) => variation.name === selectedVariation
  )?.visible_name;

  const gift = {
    ...selectedItem,
    variation: selectedVariation ?? selectedItem.variations?.[0].name ?? "",
    visible_variation: visibleVariation,
    isGiftWithSingleVariation: selectedItem.variations?.length === 1
  };

  if (selectedItem.hasMultipleOptions) {
    gift.visible_variation_2 = selectedItem.secondVariations?.find(
      (option) => option.name === selectedVariation
    )?.visible_name;
  }

  setSelectedGift(gift);
  void (await updateUserSettings(gift));
  navigateTo(NavigationType.Next);
};

const getStoreItem = (gift: MenuItem, session: Session) => {
  const metadata = session.foojiData?.campaign_user?.metadata;
  const storeItems = metadata?.available_menu_items ?? [];
  const storeItem = storeItems.find((item) => item.name_key == gift.name && item.variation_key == gift.variation);
  return storeItem;
};

export const saveSocialGiftingBundleChoices = async () => {
  const { socialGifting, socialGiftBundleSelectedItems } = get(sessionStore);

  const settings = {
    social_gift: {
      ...socialGifting,
      items_chosen: (socialGiftBundleSelectedItems || []).map((gift) => ({
        item_key: gift.itemKey,
        variation_key: gift.variation,
        count: 1
      }))
    }
  };
  await saveUserSettings(settings);
};

export const saveMultipleSelectionChoices = async () => {
  const { multipleSelectionSelectedItems } = get(sessionStore);
  await saveUserSettings({ menu_items_selected: multipleSelectionSelectedItems });
};

const saveSocialGiftingChoice = async (gift: MenuItem, session: Session) => {
  const socialGifting = session.socialGifting;
  const settings = {
    social_gift: {
      ...socialGifting,
      item_key: gift.itemKey,
      variation: gift.variation
    }
  };
  await saveUserSettings(settings);
};

const saveMenuChoice = async (gift: MenuItem, session: Session) => {
  const storeItem = getStoreItem(gift, session);
  const settings = {
    menu_item_selected: {
      item_key: gift.itemKey,
      variation_key: gift.variation,
      api: storeItem?.pickup_location?.api
    }
  };
  await saveUserSettings(settings);
};

export const updateUserSettings = async (gift: MenuItem) => {
  const session = get(sessionStore);
  const isSelectionSocialGifting = session.socialGiftingMode === "select";
  const updateAction = isSelectionSocialGifting ? saveSocialGiftingChoice : saveMenuChoice;
  await updateAction(gift, session);
};

export const onFullySpecifiedSocialGift = () => {
  clearSelectedGifts();
  navigateTo(NavigationType.Next);
};

export const clearUnavailableMultipleSelections = (availableMenuItems: MenuItem[]) => {
  get(sessionStore).multipleSelectionSelectedItems?.forEach(
    ({ item_key: itemKey, variation_key: variationKey }) => {
      if (!availableMenuItems.find((item) => (
        item.itemKey === itemKey &&
        item.variations.find((variation) => variation.name === variationKey)
      ))) {
        removeMultipleSelectionItem(itemKey);
      }
  });
};
